import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { reverse } from 'named-urls';

import AddIcon from '@mui/icons-material/Add';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import Grid from 'components/Grid';
import { Button } from 'components/Button';

import AbstractWrapper from 'components/Entities/EntityRelatedTab/components/AbstractWrapper';
import { defaultProps } from 'components/Entities/EntityRelatedTab/components/types';
import { getBasicColumnsBasedOnEntity } from 'components/Entities/EntityRelatedPage/RelatedEntities/utils';
import { auditTasksCardColumns } from 'components/Entities/EntityRelatedPage/RelatedActions/utils';

import AddActionModal from 'containers/FormsDetails/components/RelatedTab/AddActionModal';

import { entityRecordPath } from 'components/Audits/entityRecordPath';

import type AuditRecordStore from 'stores/AuditRecord';

import { TabComponentProps } from 'components/Entities/EntityDetailsContent/types';

import { useStore } from 'utils/hooks/store';

import { EntityModules, isRegisterEntityModule, RegisterEntityModule, UserRole } from 'core/constants';
import routes from 'core/routes';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import { AuditLinkEntity } from 'utils/api/modules/entities/entities.types';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { CardError, SectionsWrapper } from './styles';

// Derived, not server-driven: `details.related` is null for audits, which have no register LOOKUP
// fields. Every register type is here, matching the drawer's picker, so a hand-linked record always
// has a card to land in. Audits is not one: `get_queryset` short-circuits on the anchor, so a card
// would list the audits this one links to and never those linking to it. The link endpoint refuses it.
const RELATED_MODULES = Object.values(EntityModules).filter(isRegisterEntityModule);

// Exhaustive over RegisterEntityModule, so adding an entity type fails to compile until its
// "View all…" destination exists rather than shipping a card whose footer goes nowhere.
const RELATED_ROUTES: Record<RegisterEntityModule, string> = {
  [EntityModules.Sites]: routes.dashboard.audits.relatedSites,
  [EntityModules.Assets]: routes.dashboard.audits.relatedAssets,
  [EntityModules.Contacts]: routes.dashboard.audits.relatedContacts,
  [EntityModules.Events]: routes.dashboard.audits.relatedEvents,
  [EntityModules.RisksAssessments]: routes.dashboard.audits.relatedRiskAssessments,
  [EntityModules.Documents]: routes.dashboard.audits.relatedDocuments,
  [EntityModules.Risks]: routes.dashboard.audits.relatedRisks,
};

/** One card, owning its own load failure. The tab's `onError` is the record's — it swaps the whole
 * audit for an error page, which is far too much for one of eight cards failing to load. */
const RelatedCard: React.FunctionComponent<{
  title: string;
  testId?: string;
  headerAction?: React.ReactElement;
  footerAction?: React.ReactElement;
  children: (onCardError: () => void) => React.ReactElement;
}> = ({ title, testId, headerAction, footerAction, children }) => {
  const [failed, setFailed] = React.useState(false);

  return (
    <div data-testid={testId}>
      <AbstractWrapper
        headerTitle={title}
        headerAction={headerAction}
        gridElement={
          failed ? (
            <CardError data-testid="audit-related-card-error">{`${title} could not be loaded.`}</CardError>
          ) : (
            children(() => setFailed(true))
          )
        }
        footerAction={footerAction}
      />
    </div>
  );
};

// `onError` (the record's) is deliberately not consumed: every grid here reports into its own card.
const AuditRelatedTab: React.FunctionComponent<TabComponentProps> = ({ store }): React.ReactElement => {
  const { session } = useStore();
  const navigate = useNavigate();

  const details = (store as AuditRecordStore).details;
  const uuid = details?.uuid;
  const activeModules = session.user?.activeEntityModules || [];

  const [openModal, setOpenModal] = React.useState(false);
  const refreshTasks = React.useRef<(() => void) | null>(null);

  const onCloseModal = (created: boolean): void => {
    if (created) {
      refreshTasks.current?.();
    }
    setOpenModal(false);
  };

  const onTaskClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    navigate(reverse(routes.dashboard.tasks.details, { taskId: task.row.uuid }));
  };

  // The link endpoint rejects a migrated or never-started audit, so don't offer the action.
  const linkable = Boolean(details && !details.legacySubmission && (details.submission || details.draft));
  const canCreateTask =
    linkable && ADMIN_AND_MANAGERS.includes(session.user?.role as UserRole) && !session.user?.readOnly;

  return (
    <SectionsWrapper data-testid="audit-related-tab">
      <RelatedCard
        title="Tasks"
        headerAction={
          canCreateTask ? (
            <Button
              onClick={() => setOpenModal(true)}
              variant="text"
              size="medium"
              startIcon={<AddIcon />}
              data-testid="audit-related-new-task"
            >
              New Task
            </Button>
          ) : undefined
        }
        footerAction={
          <Button
            variant="text"
            size="medium"
            onClick={() => navigate(reverse(routes.dashboard.audits.relatedActions, { entityId: uuid || '' }))}
            data-testid="audit-related-view-all-tasks"
          >
            View all...
          </Button>
        }
      >
        {(onCardError): React.ReactElement => (
          <Grid
            onError={onCardError}
            onRowClick={onTaskClick}
            onRefreshCallback={(callback): void => {
              refreshTasks.current = callback;
            }}
            basicColumns={auditTasksCardColumns}
            dataURL={`/api/entities/${EntityModules.Audits}/${uuid}/tasks/`}
            customNoRowsText="No actions linked to this audit"
            {...defaultProps}
          />
        )}
      </RelatedCard>

      {RELATED_MODULES.filter((module) => activeModules.includes(module)).map((module) => {
        const { plural, singular } = getEntityTranslation[module];

        const onRowClick: GridEventListener<'rowClick'> = (row: GridRowParams): void => {
          const path = entityRecordPath(module, row.row.uuid);
          if (path) {
            navigate(path);
          }
        };

        return (
          <RelatedCard
            key={module}
            testId={`audit-related-card-${module}`}
            title={plural}
            footerAction={
              <Button
                variant="text"
                size="medium"
                onClick={() => navigate(reverse(RELATED_ROUTES[module], { entityId: uuid || '' }))}
                data-testid={`audit-related-view-all-${module}`}
              >
                View all...
              </Button>
            }
          >
            {(onCardError): React.ReactElement => (
              <Grid
                onError={onCardError}
                onRowClick={onRowClick}
                basicColumns={getBasicColumnsBasedOnEntity(module)}
                dataURL={`/api/entities/${EntityModules.Audits}/${uuid}/${module}/`}
                fieldsURL={`/api/entities/${module}/fields/`}
                customNoRowsText={`No ${singular.toLowerCase()} related to this audit`}
                {...defaultProps}
              />
            )}
          </RelatedCard>
        );
      })}

      {uuid && (
        <AddActionModal
          openModal={openModal}
          onClose={onCloseModal}
          links={[{ entity: AuditLinkEntity, instance: uuid }]}
        />
      )}
    </SectionsWrapper>
  );
};

export default observer(AuditRelatedTab);
